import { FormBlock, FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { Alert, BaseToggle, Button, FormGroupLabel, ToggleProps, Typography } from '@qbe/ads-core';
import React, { useState } from 'react';

import { UnorderedList, UnorderedListItem } from '~/components-ds/UnorderedList';
import ERRORS from '~/constants/errors';
import { simpleQuestionOptions, simpleQuestionOptionsBoolean } from '~/constants/questions';
import { EXTRA_INFO_MORE_BUSINESS_DETAILS_ROUTE } from '~/constants/routes';
import { useAppContent, useAppDispatch, useExtraInfo } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useHistoryQuestions } from '~/hooks/useHistoryQuestions';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { hardDecline } from '~/slices/decline';
import {
  ClaimCount,
  claimCountOptions,
  updateApplied,
  updateClaimCount,
  updateHistoryAnswers,
} from '~/slices/extraInfo';
import { qalFormCompleteAction, qalFormStartAction, qalTrackEventAction } from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { SimpleQuestionAnswer } from '~/types/enums';
import {
  getExtraCustomerDetails,
  trackCustomerHistory,
} from '~/utils/analytics/getSlices/getExtraCustomerDetails';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

const ExtraInfoCustomerHistory: React.FC = () => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme.extraInfoCustomerHistory',
  });

  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { sortedHeaderQuestions, sortedSubQuestions } = useHistoryQuestions();

  const { claimCount, applied, historyAnswers, historyQuestions } = useExtraInfo();
  const [claimValue, setClaimValue] = useState(claimCount ?? '');
  const [showWarning, setShowWarning] = useState(false);
  const [individualQuestionsOpen, setIndividualQuestionsOpen] = useState(false);
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  const nextDisabled =
    !claimCount ||
    applied === null ||
    (applied && historyAnswers?.some((a) => a.answer === undefined));

  const resetHistoryAnswers = () => {
    dispatch(
      updateHistoryAnswers(
        sortedSubQuestions.map((q) => ({
          id: q.questionId,
          answer: undefined,
        }))
      )
    );
  };

  const handleNext = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    handleFormComplete();
    getExtraCustomerDetails(claimCount, applied, dispatch);
    if (claimCount === ClaimCount.TwoPlus) {
      dispatch(hardDecline(ERRORS.noOfclaims));
      return;
    }

    if (applied && !individualQuestionsOpen) {
      resetHistoryAnswers();
      setIndividualQuestionsOpen(true);
      setShowWarning(false);
      return;
    }

    if (applied && individualQuestionsOpen)
      trackCustomerHistory(historyAnswers, historyQuestions, dispatch);

    if (applied && individualQuestionsOpen && historyAnswers?.every((a) => a.answer === false)) {
      setIndividualQuestionsOpen(false);
      setShowWarning(true);
      dispatch(
        qalTrackEventAction({
          event: 'submit',
          eventAction: `warning error - extra customer history`,
          eventLabel:
            "As you've answered 'No' to all questions, please review your previous answer.",
          data: {},
        })
      );
      return;
    }

    if (applied && individualQuestionsOpen && historyAnswers?.some((a) => a.answer === true)) {
      dispatch(hardDecline(ERRORS.historyQuestions));
      return;
    }

    dispatch(
      navigateAction({
        to: EXTRA_INFO_MORE_BUSINESS_DETAILS_ROUTE,
        navigate,
      })
    );
  };

  const setIndividualAnswer = (id: string, val: string) => {
    const newAnswers = historyAnswers!.map((a) =>
      a.id !== id
        ? a
        : {
            id: a.id,
            answer: val === SimpleQuestionAnswer.Yes,
          }
    );
    dispatch(updateHistoryAnswers(newAnswers));
  };

  const handleChange = (isApplied: boolean) => {
    handleFormStart();
    dispatch(updateApplied(isApplied));
    resetHistoryAnswers();

    setIndividualQuestionsOpen(isApplied);

    if (isApplied) {
      navigate('#customer-history-questions');
      return;
    }
    setShowWarning(false);
  };
  const [firstQuestion, secondQuestion] = sortedHeaderQuestions;

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <FormCard data-testid="customer-history" variant="sectionTitle" title={getTextContent('title')}>
      <FormBlock preset="toggle">
        <Toggle
          name="claimCount"
          optionList={claimCountOptions.map(
            ({ text, value }): ToggleProps['optionList'][number] => ({
              id: `claim-count-${text}`,
              label: text,
              value,
              defaultChecked: claimValue === value,
              onChange: (e) => {
                const { value: v } = e.target;
                handleFormStart();
                setClaimValue(v);
                dispatch(updateClaimCount(v));
                navigate('#customer-history-questions');
              },
            })
          )}
          title={firstQuestion?.questionGroupText}
          data-track={firstQuestion?.questionGroupText}
          data-track-value="capture"
        />
      </FormBlock>

      <FormGroupLabel
        id="customer-history-questions"
        title={
          individualQuestionsOpen
            ? getTextContent('content.appliedQuestion.title')
            : secondQuestion?.questionGroupText
        }
      />
      <UnorderedList>
        {sortedSubQuestions.map((q) => (
          <UnorderedListItem key={q.questionId}>
            <Typography component="span" variant="body1">
              {q.questionText}
            </Typography>
            {individualQuestionsOpen && (
              <BaseToggle
                name={`question-${q.questionId}`}
                optionList={simpleQuestionOptions.map(
                  ({ text, value }): ToggleProps['optionList'][number] => ({
                    id: `question-${q.questionId}-${value}'`,
                    label: text,
                    value,
                    onChange: (e) => setIndividualAnswer(q.questionId, e.target.value),
                  })
                )}
                data-track={secondQuestion?.questionGroupText}
                data-track-value="capture"
              />
            )}
          </UnorderedListItem>
        ))}
      </UnorderedList>

      {!individualQuestionsOpen && (
        <FormBlock preset="toggle">
          <BaseToggle
            name="aiedValue"
            optionList={simpleQuestionOptionsBoolean.map(
              ({ text, value }): ToggleProps['optionList'][number] => ({
                id: `appliedValue-${value}`,
                label: text,
                value: value.toString(),
                onChange: (e) => handleChange(JSON.parse(e.target.value)),
                defaultChecked: value === applied,
              })
            )}
            disabled={!claimCount}
            data-track={secondQuestion?.questionGroupText}
            data-track-value="capture"
          />
        </FormBlock>
      )}

      {showWarning && (
        <Alert variant="warning" heading={getTextContent('content.historyWarning.description')} />
      )}

      <FormBlock preset="button">
        <Button
          label={getSharedContent('nextStepButtonLabel')}
          data-track="next"
          onClick={handleNext}
          disabled={nextDisabled}
        />
      </FormBlock>
    </FormCard>
  );
};

export default ExtraInfoCustomerHistory;
